/**
 * One description of an order action's type — its icon, its short label and the
 * tooltip the order-screen form shows — so the order form, the Sales listing and
 * the Sales diary cannot drift on how a CALL or a REMINDER is drawn.
 *
 * Every Sales surface offers every type, site inspections included. The helpers
 * below still take an explicit list rather than deriving from every key, because
 * the order in which the types are presented is part of the contract — the
 * order-screen radio group has always drawn them in this order.
 */
export const ACTION_TYPE_META = {
    CALL: { label: 'Call', tooltip: 'Phone call', icon: 'system-phone' },
    EMAIL: { label: 'Email', tooltip: 'Email', icon: 'system-email-filled' },
    SMS: { label: 'SMS', tooltip: 'SMS message', icon: 'system-mobile' },
    REMINDER: { label: 'Reminder', tooltip: 'Reminder', icon: 'system-notification-filled' },
    SITE_INSPECTION: { label: 'Site inspection', tooltip: 'Site inspection', icon: 'system-site-inspection-calendar' },
};

/** The four types that are pure follow-ups — no staff member, no duration. */
export const SALES_ACTION_TYPES = ['CALL', 'EMAIL', 'SMS', 'REMINDER'];

/** Every type, in the order the order-screen form has always presented them. */
export const ALL_ACTION_TYPES = [...SALES_ACTION_TYPES, 'SITE_INSPECTION'];

/**
 * Shown for a type this map has never heard of, rather than rendering nothing:
 * an unknown `action_type` is a data problem worth seeing, not worth hiding.
 */
const FALLBACK_ICON = 'system-action';

export function actionTypeIcon(type) {
    return ACTION_TYPE_META[type]?.icon ?? FALLBACK_ICON;
}

export function actionTypeLabel(type) {
    return ACTION_TYPE_META[type]?.label ?? type;
}

/**
 * `{ CALL: 'Call', … }` — the option-map shape MuvalSelect takes.
 */
export function actionTypeOptions(types = SALES_ACTION_TYPES) {
    return Object.fromEntries(types.map((type) => [type, actionTypeLabel(type)]));
}

/**
 * The icon-radio list the order-screen form renders. `id` is the position in the
 * list because the radio group keys on it.
 */
export function actionTypeRadioList(types = ALL_ACTION_TYPES) {
    return types.map((type, id) => ({
        id,
        value: type,
        icon: actionTypeIcon(type),
        tooltip: ACTION_TYPE_META[type]?.tooltip ?? actionTypeLabel(type),
    }));
}
